/**
 * form_guard_controller.js — a create form says what it still needs, as it is filled in (Stimulus).
 *
 * Mounted on the <form> of every create form (quote, job): one reading of "what
 * stops this form submitting", shown three ways at once.
 *   - The Create button grays while a required field is empty. A class only,
 *     never disabled or aria-disabled: base.css and Turbo make an aria-disabled
 *     button untappable, and the tap is what lists the missing fields.
 *   - A tap on Create that cannot submit marks each empty field inline and
 *     writes "Still needed: ..." beside the buttons. On a phone the form is a
 *     bottom sheet and the field with the error can be out of view, so without
 *     the summary a refused tap looks like a dead button.
 *   - Fixing a field clears its own mark as the person types.
 *
 * WHAT COUNTS AS REQUIRED
 * A field's `required` flag, or `data-required="true"` on a select whose native
 * element a widget hides (Choices.js), which the browser could not focus to
 * report. The flags are NOT decided here: client-mode and client-type own the
 * ones inside the client block, so a flag on a field their mode has hidden is
 * already off. Nothing in the rendered markup carries `required` beyond what the
 * friction ceiling in tests/e2e/test_required_inputs.py allows.
 *
 * The form must be `novalidate`. Native validation runs before `submit` fires,
 * so with it on this controller would never be asked, and the browser's own
 * bubble (absent on some native web views) would be the only message.
 *
 * Markup: an optional [data-form-guard-target="summary"] beside the buttons
 * (templates/macros/create_form.html renders it). A field's name in the summary
 * is its <label for>, or data-form-guard-label when the label reads as a
 * sentence ("Select Client").
 *
 * OTHER CODE'S WAY IN
 * Code that knows something the form cannot (the server says this email
 * already belongs to a client) marks a field through events on the form rather
 * than reaching into this controller:
 *   form-guard:flag   detail { field, message }  mark the field, list it
 *   form-guard:clear  detail { field }           unmark it
 * A `reset` of the form clears every mark, so a cancelled create reopens clean.
 *
 * UNSAVED WORK
 * Once anything is typed or picked, leaving the page asks first
 * (./_unsaved_work.js), until the server takes the save. A save in flight is
 * not unsaved work: Turbo follows a successful create's redirect before it
 * reports the submit's end, so the guard would otherwise ask on success. A
 * failed save makes it unsaved work again; the form keeps everything (only
 * success resets it), and a reset (success, or a discarded cancel) ends it.
 */
import { Controller } from "@hotwired/stimulus";
import { guardUnsavedWork, releaseUnsavedWork, unsavedWorkChanged } from "./_unsaved_work.js";

const REQUIRED_SELECTOR = '[required], select[data-required="true"]';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class extends Controller {
    static targets = ['summary'];

    connect() {
        this._onSubmit = (event) => this._submit(event);
        this._onEdit = (event) => this._edited(event.target, event.isTrusted);
        this._onReset = () => setTimeout(() => this._cleared());
        this._onFlag = (event) => this._flagged(event.detail);
        this._onClear = (event) => this._unflagged(event.detail);
        this._onSubmitStart = (event) => { if (event.target === this.element) this._setUnsaved(false); };
        this._onSubmitEnd = (event) => {
            if (event.target === this.element && !event.detail.success) this._setUnsaved(true);
        };

        this.element.addEventListener('submit', this._onSubmit);
        this.element.addEventListener('input', this._onEdit);
        this.element.addEventListener('change', this._onEdit);
        this.element.addEventListener('reset', this._onReset);
        this.element.addEventListener('form-guard:flag', this._onFlag);
        this.element.addEventListener('form-guard:clear', this._onClear);
        this.element.addEventListener('turbo:submit-start', this._onSubmitStart);
        this.element.addEventListener('turbo:submit-end', this._onSubmitEnd);
        this._unsaved = false;
        guardUnsavedWork(this.element, () => this._unsaved);
        this._showReady();
    }

    disconnect() {
        this.element.removeEventListener('submit', this._onSubmit);
        this.element.removeEventListener('input', this._onEdit);
        this.element.removeEventListener('change', this._onEdit);
        this.element.removeEventListener('reset', this._onReset);
        this.element.removeEventListener('form-guard:flag', this._onFlag);
        this.element.removeEventListener('form-guard:clear', this._onClear);
        this.element.removeEventListener('turbo:submit-start', this._onSubmitStart);
        this.element.removeEventListener('turbo:submit-end', this._onSubmitEnd);
        releaseUnsavedWork(this.element);
    }

    _setUnsaved(flag) {
        if (this._unsaved === flag) return;
        this._unsaved = flag;
        unsavedWorkChanged();
    }

    _submit(event) {
        const failures = this._failures();
        this._marked().forEach((field) => {
            if (!failures.some((failure) => failure.field === field)) this._unmark(field);
        });
        failures.forEach(({ field, message }) => this._mark(field, message));
        this._summarise();
        if (!failures.length) return;

        event.preventDefault();
        event.stopPropagation();
        const first = this._container(failures[0].field);
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof failures[0].field.focus === 'function') failures[0].field.focus();
    }

    /** Every required field that is empty, then every typed email that is not one. */
    _failures() {
        const empty = Array.from(this.element.querySelectorAll(REQUIRED_SELECTOR))
            .filter((field) => this._isEmpty(field))
            .map((field) => ({
                field,
                message: field.tagName === 'SELECT' ? 'Please choose one.' : 'Please fill out this field.',
            }));
        const badEmails = Array.from(this.element.querySelectorAll('input[type="email"]'))
            .filter((field) => field.getClientRects().length && field.value.trim()
                && !EMAIL_PATTERN.test(field.value.trim()))
            .map((field) => ({ field, message: 'Please enter a valid email address.' }));
        return [...empty, ...badEmails];
    }

    _isEmpty(field) {
        return !field.value.trim();
    }

    // Only the person's own edits are unsaved work: scripts dispatch change
    // events of their own as the form sets up (pickers, client mode), and the
    // description editor's typing reaches here as the editable's own input.
    _edited(field, byPerson) {
        if (byPerson) this._setUnsaved(true);
        this._unmark(field);
        this._summarise();
        this._showReady();
    }

    _cleared() {
        this._setUnsaved(false);
        this._marked().forEach((field) => this._unmark(field));
        this._summarise();
        this._showReady();
    }

    _flagged({ field, message }) {
        if (!field) return;
        this._mark(field, message);
        this._summarise();
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    _unflagged({ field }) {
        if (!field) return;
        this._unmark(field);
        this._summarise();
    }

    /** Choices.js wraps the native select it hides, and the rich description
     *  editor the textarea it mirrors into; the wrapper is what shows the mark. */
    _container(field) {
        return field.closest('.choices, .rich-description') || field;
    }

    _mark(field, message) {
        const container = this._container(field);
        container.classList.add('is-invalid');
        let feedback = container.parentNode.querySelector(':scope > .invalid-feedback');
        if (!feedback) {
            feedback = document.createElement('div');
            feedback.className = 'invalid-feedback';
            container.parentNode.appendChild(feedback);
        }
        feedback.textContent = message;
        feedback.style.display = 'block';
    }

    _unmark(field) {
        const container = this._container(field);
        container.classList.remove('is-invalid');
        const feedback = container.parentNode.querySelector(':scope > .invalid-feedback');
        if (feedback) feedback.style.display = 'none';
    }

    _marked() {
        return Array.from(this.element.querySelectorAll('.is-invalid'), (container) =>
            container.matches('.choices') ? container.querySelector('select') : container);
    }

    _summarise() {
        if (!this.hasSummaryTarget) return;
        const names = this._marked().map((field) => this._label(field));
        this.summaryTarget.textContent = names.length ? `Still needed: ${names.join(', ')}.` : '';
        this.summaryTarget.hidden = names.length === 0;
    }

    _label(field) {
        if (field.dataset.formGuardLabel) return field.dataset.formGuardLabel;
        const label = field.id && this.element.querySelector(`label[for="${field.id}"]`);
        return label ? label.textContent.replace('*', '').trim() : field.name;
    }

    _showReady() {
        const button = this.element.querySelector('button[type="submit"]');
        if (!button) return;
        const incomplete = Array.from(this.element.querySelectorAll(REQUIRED_SELECTOR))
            .some((field) => this._isEmpty(field));
        button.classList.toggle('is-incomplete', incomplete);
    }
}
